import { Trans } from '@lingui/macro'
import { useWeb3React } from '@web3-react/core'
import { CurrencyAmount } from '@x-swap-protocol/sdk-core'
import MigrationUsdcJson from 'abis/migration-usdc.json'
import { MigrationUsdc } from 'abis/types'
import { ButtonGray, ButtonLight, ButtonPrimary } from 'components/Button'
import { Input as NumericalInput } from 'components/NumericalInput'
import { darken } from 'polished'
import { useCallback, useMemo } from 'react'
import { ArrowDown } from 'react-feather'
import styled, { useTheme } from 'styled-components/macro'
import { flexColumnNoWrap, flexRowNoWrap } from 'theme/styles'

import { useToggleAccountDrawer } from '../../components/AccountDrawer'
import { AutoColumn } from '../../components/Column'
import { OpacityHoverState } from '../../components/Common'
import { FiatValue } from '../../components/CurrencyInputPanel/FiatValue'
import { LoadingOpacityContainer, loadingOpacityMixin } from '../../components/Loader/styled'
import CurrencyLogo from '../../components/Logo/CurrencyLogo'
import { RowBetween, RowFixed } from '../../components/Row'
import { ArrowWrapper, Dots } from '../../components/swap/styleds'
import { getChainInfo } from '../../constants/chainInfo'
import { SupportedChainId } from '../../constants/chains'
import { NATIVE_USDC_XDC, USDC_XDC } from '../../constants/tokens'
import { useCurrency } from '../../hooks/Tokens'
import { ApprovalState, useApproveCallback } from '../../hooks/useApproveCallback'
import { useContract } from '../../hooks/useContract'
import { useSwitchChain } from '../../hooks/useSwitchChain'
import { useUSDPrice } from '../../hooks/useUSDPrice'
import { useTokenBalancesWithLoadingIndicator } from '../../state/connection/hooks'
import { useAppSelector } from '../../state/hooks'
import { useTransactionAdder } from '../../state/transactions/hooks'
import { TransactionType } from '../../state/transactions/types'
import { ThemedText } from '../../theme'
import { Z_INDEX } from '../../theme/zIndex'
import { formatCurrencyAmount } from '../../utils/formatCurrencyAmount'
import { didUserReject } from '../../utils/swapErrorToUserReadableMessage'
import { ArrowContainer } from '../Swap'

const BaseLayout = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 0 16px 52px;

  justify-content: center;
  width: 100%;

  @media screen and (min-width: ${({ theme }) => theme.breakpoint.sm}px) {
    gap: 16px;
    padding: 100px 8px 52px;
  }
`
const LeftPanel = styled.div`
  flex: 1;
  max-width: 780px;
  min-width: 360px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
`
const RightPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 360px;
  padding: 10px;
`
const InputPanel = styled.div<{ hideInput?: boolean }>`
  ${flexColumnNoWrap};
  position: relative;
  border-radius: ${({ hideInput }) => (hideInput ? '16px' : '20px')};
  z-index: 1;
  width: ${({ hideInput }) => (hideInput ? '100%' : 'initial')};
  transition: height 1s ease;
  will-change: height;
`

const Container = styled.div<{ hideInput: boolean }>`
  min-height: 44px;
  border-radius: ${({ hideInput }) => (hideInput ? '16px' : '20px')};
  width: ${({ hideInput }) => (hideInput ? '100%' : 'initial')};
`

const CurrencySelect = styled(ButtonGray)<{
  visible: boolean
  selected: boolean
  hideInput?: boolean
  disabled?: boolean
}>`
  align-items: center;
  background-color: ${({ selected, theme }) => (selected ? theme.backgroundInputPanel : theme.blue400)};
  opacity: ${({ disabled }) => (!disabled ? 1 : 0.4)};
  box-shadow: ${({ selected }) => (selected ? 'none' : '0px 6px 10px rgba(0, 0, 0, 0.075)')};
  color: ${({ selected, theme }) => (selected ? theme.textPrimary : theme.white)};
  cursor: pointer;
  height: unset;
  border-radius: 22px;
  outline: none;
  user-select: none;
  border: none;
  font-size: 16px;
  font-weight: 500;
  width: ${({ hideInput }) => (hideInput ? '100%' : 'initial')};
  padding: ${({ selected }) => (selected ? '3px 5px' : '4px 7px')};
  gap: 8px;
  justify-content: space-between;
  margin-left: ${({ hideInput }) => (hideInput ? '0' : '12px')};

  &:hover,
  &:active {
    background-color: ${({ theme, selected }) => (selected ? theme.backgroundInteractive : '#5F60D4')};
  }

  &:before {
    background-size: 100%;
    border-radius: inherit;

    position: absolute;
    top: 0;
    left: 0;

    width: 100%;
    height: 100%;
    content: '';
  }

  &:hover:before {
    background-color: ${({ theme }) => theme.stateOverlayHover};
  }

  &:active:before {
    background-color: ${({ theme }) => theme.stateOverlayPressed};
  }

  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
`

const InputRow = styled.div`
  ${flexRowNoWrap};
  align-items: center;
  justify-content: space-between;
`

const LabelRow = styled.div`
  ${flexRowNoWrap};
  align-items: center;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.75rem;
  line-height: 1rem;

  span:hover {
    cursor: pointer;
    color: ${({ theme }) => darken(0.2, theme.textSecondary)};
  }
`

const FiatRow = styled(LabelRow)`
  justify-content: flex-end;
  min-height: 20px;
  padding: 8px 0px 0px 0px;
`

const Aligner = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const StyledTokenName = styled.span<{ active?: boolean }>`
  margin-left: 5px;
  font-weight: ${({ active }) => (active ? 600 : 500)};
  font-size: ${({ active }) => (active ? '18px' : '16px')};
  line-height: ${({ active }) => (active ? '24px' : '26px')};
`

const StyledNumericalInput = styled(NumericalInput)<{ $loading: boolean }>`
  ${loadingOpacityMixin};
  text-align: left;
  font-size: 36px;
  line-height: 44px;
  font-variant: small-caps;
`

const SwapSection = styled.div`
  position: relative;
  background-color: ${({ theme }) => theme.backgroundModule};
  border-radius: 12px;
  padding: 16px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;

  &:before {
    box-sizing: border-box;
    background-size: 100%;
    border-radius: inherit;

    position: absolute;
    top: 0;
    left: 0;

    width: 100%;
    height: 100%;
    pointer-events: none;
    content: '';
    border: 1px solid ${({ theme }) => theme.backgroundModule};
  }

  &:hover:before {
    border-color: ${({ theme }) => theme.stateOverlayHover};
  }

  &:focus-within:before {
    border-color: ${({ theme }) => theme.stateOverlayPressed};
  }
`

const SwapWrapper = styled.main<{ chainId?: number }>`
  position: relative;
  background: ${({ theme }) => theme.backgroundSurface};
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  padding: 8px;
  padding-top: 12px;
  z-index: ${Z_INDEX.default};
  transition: transform 250ms ease;

  &:hover {
    border: 1px solid ${({ theme }) => theme.backgroundOutline};
  }
`

const Link = styled.a`
  color: ${({ theme }) => theme.textPrimary};

  ${OpacityHoverState}
`

const WarningBox = styled.div`
  background-color: ${({ theme }) => theme.yellow300};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  font-weight: 500;
`

// const MigrationContractAddress = '0x334ad0f30dd6997C763527DA1373476F3d2013B2'
const MigrationContractAddress = '0x10D0d006319699D5A68627863d623f32C359Ce74' // transfer to foundation

export default function MigrateUSDC() {
  const theme = useTheme()
  const addTransaction = useTransactionAdder()
  const { account, chainId, connector } = useWeb3React()
  const switchChain = useSwitchChain()
  const toggleWalletDrawer = useToggleAccountDrawer()
  const switchingChain = useAppSelector((state) => state.wallets.switchingChain)

  const usdceCurrency = useCurrency(USDC_XDC.address)
  const usdcCurrency = useCurrency(NATIVE_USDC_XDC.address)

  const [balances, isLoading] = useTokenBalancesWithLoadingIndicator(account ?? undefined, [USDC_XDC, NATIVE_USDC_XDC])

  const usdceBalance = useMemo(() => {
    if (!balances || !balances[USDC_XDC.address]) {
      return undefined
    }
    return balances[USDC_XDC.address]
  }, [balances])

  const [approval, approveCallback] = useApproveCallback(usdceBalance, MigrationContractAddress)
  const contract = useContract<MigrationUsdc>(MigrationContractAddress, MigrationUsdcJson.abi)

  const usdcBalance = useMemo(() => {
    if (!balances || !balances[NATIVE_USDC_XDC.address]) {
      return undefined
    }
    return balances[NATIVE_USDC_XDC.address]
  }, [balances])

  const chainAllowed = useMemo(() => {
    return chainId === SupportedChainId.XDC
  }, [chainId])

  const usdceValue = useMemo(() => {
    if (isLoading || !balances) return ''
    return balances[USDC_XDC.address]?.toFixed(6) ?? ''
  }, [balances, isLoading])

  const usdcValue = useMemo(() => {
    if (isLoading || !balances) return ''
    return usdceBalance?.toExact() ?? ''
  }, [balances, isLoading, usdceBalance])

  const fiatValueUSDCE = useUSDPrice(usdceBalance)
  const fiatValueUSDC = useUSDPrice(CurrencyAmount.fromRawAmount(NATIVE_USDC_XDC, usdceBalance?.quotient ?? '0'))

  const handleMigrate = useCallback(async () => {
    if (!contract || !usdceBalance) {
      return
    }

    try {
      const response = await contract.convert(usdceBalance.quotient.toString())
      if (response) {
        addTransaction(response, {
          type: TransactionType.MIGRATE_USDC,
          usdceAmount: usdceBalance.quotient.toString(),
          usdceAddress: USDC_XDC.address,
          usdcAmount: usdceBalance.quotient.toString(),
          usdcAddress: NATIVE_USDC_XDC.address,
        })
      }
    } catch (e) {
      console.log(e)
    }
  }, [contract, usdceBalance, addTransaction])

  const handleApproveAndMigrate = useCallback(async () => {
    await approveCallback()
    await handleMigrate()
  }, [approveCallback, handleMigrate])

  return (
    <BaseLayout>
      <LeftPanel>
        <ThemedText.DeprecatedLargeHeader>
          <Trans>USDC.e to Native USDC Migration</Trans>
        </ThemedText.DeprecatedLargeHeader>
        <WarningBox>
          <ThemedText.DeprecatedBody style={{ fontWeight: 600 }}>
            <Trans>
              ⚠️ Attention! Stargate Bridged USDC is not supported on our platform. Please withdraw via the existing
              Stargate Bridge.
            </Trans>
          </ThemedText.DeprecatedBody>
        </WarningBox>

        <ThemedText.DeprecatedBody style={{ marginBottom: 8, fontWeight: 400, opacity: 0.8 }}>
          <Trans>
            Migrate your <b>USDC.e</b> (bridged USDC) tokens to <b>Native USDC</b> directly on XDC network. This
            migration provides improved functionality and reduced transaction costs. Also you can use bridge platform
            (bridge.xdc.org) to send your USDC.e to Arbitrum and Ethereum networks.
          </Trans>
        </ThemedText.DeprecatedBody>
        <ThemedText.DeprecatedMediumHeader>
          <Trans>Migration Details</Trans>
        </ThemedText.DeprecatedMediumHeader>
        <ThemedText.DeprecatedBody style={{ marginBottom: 8, fontWeight: 400 }}>
          <ul>
            <li>
              <b>Conversion Rate: 1 USDC.e = 1 Native USDC</b> (1:1 ratio)
            </li>
            <li>
              Migration is available on <b>XDC Network</b> only
            </li>
            <li>
              <b>Migration is supported only on this page.</b>
            </li>
          </ul>
        </ThemedText.DeprecatedBody>
      </LeftPanel>
      <RightPanel>
        <SwapWrapper>
          <SwapSection>
            <InputPanel>
              <Container hideInput={false}>
                <InputRow>
                  <StyledNumericalInput
                    className="token-amount-input"
                    value={usdceValue}
                    onUserInput={() => null}
                    disabled={!chainAllowed}
                    $loading={isLoading}
                  />

                  <CurrencySelect
                    disabled={true}
                    visible={usdceCurrency !== undefined}
                    selected={!!usdceCurrency}
                    hideInput={false}
                    onClick={() => null}
                  >
                    <Aligner>
                      <RowFixed>
                        <CurrencyLogo style={{ marginRight: '2px' }} currency={usdceCurrency} size="24px" />

                        <StyledTokenName
                          className="token-symbol-container"
                          active={Boolean(usdceCurrency && usdceCurrency.symbol)}
                        >
                          {(usdceCurrency && usdceCurrency.symbol && usdceCurrency.symbol.length > 20
                            ? usdceCurrency.symbol.slice(0, 4) +
                              '...' +
                              usdceCurrency.symbol.slice(usdceCurrency.symbol.length - 5, usdceCurrency.symbol.length)
                            : usdceCurrency?.symbol) || <Trans>Select token</Trans>}
                        </StyledTokenName>
                      </RowFixed>
                    </Aligner>
                  </CurrencySelect>
                </InputRow>
                <FiatRow>
                  <RowBetween>
                    <LoadingOpacityContainer $loading={isLoading}>
                      {fiatValueUSDCE && <FiatValue fiatValue={fiatValueUSDCE} />}
                    </LoadingOpacityContainer>
                    {account ? (
                      <RowFixed style={{ height: '30px' }}>
                        <ThemedText.DeprecatedBody
                          color={theme.textSecondary}
                          fontWeight={400}
                          fontSize={14}
                          style={{ display: 'inline', color: theme.textTertiary }}
                        >
                          {usdceCurrency && usdceBalance ? (
                            <Trans>Balance: {formatCurrencyAmount(usdceBalance, 6)}</Trans>
                          ) : null}
                        </ThemedText.DeprecatedBody>
                      </RowFixed>
                    ) : (
                      <span />
                    )}
                  </RowBetween>
                </FiatRow>
              </Container>
            </InputPanel>
          </SwapSection>
          <ArrowWrapper clickable={false}>
            <ArrowContainer
              data-testid="swap-currency-button"
              onClick={() => {
                null
              }}
              color={theme.textPrimary}
            >
              <ArrowDown size="16" color={theme.textPrimary} />
            </ArrowContainer>
          </ArrowWrapper>
          <AutoColumn gap="sm">
            <SwapSection>
              <InputPanel>
                <Container hideInput={false}>
                  <InputRow>
                    <StyledNumericalInput
                      className="token-amount-input"
                      value={usdcValue}
                      onUserInput={() => null}
                      disabled={!chainAllowed}
                      $loading={isLoading}
                    />

                    <CurrencySelect
                      disabled={true}
                      visible={usdcCurrency !== undefined}
                      selected={!!usdcCurrency}
                      hideInput={false}
                      onClick={() => null}
                    >
                      <Aligner>
                        <RowFixed>
                          <CurrencyLogo style={{ marginRight: '2px' }} currency={usdcCurrency} size="24px" />

                          <StyledTokenName
                            className="token-symbol-container"
                            active={Boolean(usdcCurrency && usdcCurrency.symbol)}
                          >
                            {(usdcCurrency && usdcCurrency.symbol && usdcCurrency.symbol.length > 20
                              ? usdcCurrency.symbol.slice(0, 4) +
                                '...' +
                                usdcCurrency.symbol.slice(usdcCurrency.symbol.length - 5, usdcCurrency.symbol.length)
                              : usdcCurrency?.symbol) || <Trans>Select token</Trans>}
                          </StyledTokenName>
                        </RowFixed>
                      </Aligner>
                    </CurrencySelect>
                  </InputRow>
                  <FiatRow>
                    <RowBetween>
                      <LoadingOpacityContainer $loading={isLoading}>
                        {fiatValueUSDC && <FiatValue fiatValue={fiatValueUSDC} />}
                      </LoadingOpacityContainer>
                      {account ? (
                        <RowFixed style={{ height: '30px' }}>
                          <ThemedText.DeprecatedBody
                            color={theme.textSecondary}
                            fontWeight={400}
                            fontSize={14}
                            style={{ display: 'inline', color: theme.textTertiary }}
                          >
                            {usdcCurrency && usdcBalance ? (
                              <Trans>Balance: {formatCurrencyAmount(usdcBalance, 6)}</Trans>
                            ) : null}
                          </ThemedText.DeprecatedBody>
                        </RowFixed>
                      ) : (
                        <span />
                      )}
                    </RowBetween>
                  </FiatRow>
                </Container>
              </InputPanel>
            </SwapSection>
            <div>
              {switchingChain ? (
                <ButtonPrimary disabled={true}>
                  <Trans>Connecting to {getChainInfo(switchingChain)?.label}</Trans>{' '}
                </ButtonPrimary>
              ) : !account ? (
                <ButtonLight onClick={toggleWalletDrawer} fontWeight={600}>
                  <Trans>Connect Wallet</Trans>
                </ButtonLight>
              ) : chainId && chainId !== SupportedChainId.XDC ? (
                <ButtonPrimary
                  onClick={async () => {
                    try {
                      await switchChain(connector, SupportedChainId.XDC)
                    } catch (error) {
                      if (didUserReject(error)) {
                        // Ignore error, which keeps the user on the previous chain.
                      } else {
                        // TODO(WEB-3306): This UX could be improved to show an error state.
                        throw error
                      }
                    }
                  }}
                >
                  Connect to {getChainInfo(SupportedChainId.XDC)?.label}
                </ButtonPrimary>
              ) : approval === ApprovalState.NOT_APPROVED ? (
                <ButtonPrimary onClick={handleApproveAndMigrate}>Approve USDC.e & Migrate</ButtonPrimary>
              ) : approval === ApprovalState.PENDING ? (
                <ButtonPrimary disabled>
                  Approving <Dots />
                </ButtonPrimary>
              ) : (
                <ButtonPrimary disabled={usdceBalance?.lessThan('1')} onClick={handleMigrate}>
                  Migrate
                </ButtonPrimary>
              )}
            </div>
          </AutoColumn>
        </SwapWrapper>
      </RightPanel>
    </BaseLayout>
  )
}
